import React, { useState, useEffect, useRef } from 'react';
import { GMap } from 'primereact/gmap';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { loadGoogleMapsEmpleado } from '../DatosEmpleado/GoogleMapsEmpleado';

const DibujadorDeRuta = ({ rutaInicial = '[]', onConfirmar, onCancelar, soloConsulta = false, datRuta = {} }) => {
    const [mapReady, setMapReady] = useState(false);
    const [puntos, setPuntos] = useState([]);
    const [overlays, setOverlays] = useState([]);
    const [currentCenter, setCurrentCenter] = useState({ lat: -0.180653, lng: -78.467834 });
    const [searchQuery, setSearchQuery] = useState('');
    const [nombre, setNombre] = useState('');

    const mapRef = useRef(null);
    const directionsRendererRef = useRef(null);
    const directionsServiceRef = useRef(null);
    const inputSearchRef = useRef(null);
    const toast = useRef(null);

    // 1. CARGA INICIAL
    useEffect(() => {
        loadGoogleMapsEmpleado(() => {
            if (!directionsServiceRef.current) {
                directionsServiceRef.current = new window.google.maps.DirectionsService();
            }
            if (!directionsRendererRef.current) {
                directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
                    draggable: false, // Lo manejamos nosotros manualmente para mejor control
                    suppressMarkers: true // Usamos nuestros propios marcadores con números
                });
            }

            // Parsear ruta inicial para EDICIÓN
            try {
                if (rutaInicial && rutaInicial !== '[]') {
                    const puntosParseados = JSON.parse(rutaInicial);
                    if (Array.isArray(puntosParseados) && puntosParseados.length > 0) {
                        setPuntos(puntosParseados);
                        setCurrentCenter(puntosParseados[0]);
                    }
                }
            } catch (e) {
                console.error('Error cargando ruta inicial', e);
            }

            setMapReady(true);
            inicializarBuscador();
        });
    }, [rutaInicial]);

    // 2. RE-DIBUJAR CUANDO CAMBIAN LOS PUNTOS (CORREGIDO)
    useEffect(() => {
        if (mapReady && window.google) {
            actualizarVisualesManuales(puntos);
        }
    }, [puntos, mapReady]);

    const inicializarBuscador = () => {
        setTimeout(() => {
            const inputElement = inputSearchRef.current?.getElement?.() || inputSearchRef.current;
            if (inputElement && window.google?.maps?.places) {
                const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
                    fields: ['geometry', 'formatted_address'],
                    types: ['geocode', 'establishment']
                });

                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (place.geometry?.location) {
                        const newPos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };

                        // Mover mapa sin recrearlo
                        if (mapRef.current) mapRef.current.panTo(newPos);

                        setSearchQuery(place.formatted_address);
                        mostrarPinBusqueda(newPos);
                    }
                });
            }
        }, 600);
    };

    const mostrarPinBusqueda = (pos) => {
        const markerBusqueda = new window.google.maps.Marker({
            position: pos,
            icon: { url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png' } // Pin Azul
        });
        markerBusqueda.set('tipo', 'busqueda');
        setOverlays((prev) => [...prev.filter((o) => o.get('tipo') !== 'busqueda'), markerBusqueda]);
    };

    // 3. LÓGICA DE DIBUJO Y EDICIÓN (REHECHA)
    const actualizarVisualesManuales = (lista) => {
        const nuevosOverlays = [];

        // Mantener el pin de búsqueda si existe
        const pinBusquedaActual = overlays.find((o) => o.get('tipo') === 'busqueda');
        if (pinBusquedaActual) nuevosOverlays.push(pinBusquedaActual);

        // Dibujar Polilínea Manual (Línea azul entre puntos)
        if (lista.length > 1) {
            nuevosOverlays.push(
                new window.google.maps.Polyline({
                    path: lista,
                    geodesic: true,
                    strokeColor: '#334eff',
                    strokeOpacity: 0.6,
                    strokeWeight: 4
                })
            );
        }

        // Crear Marcadores para cada punto
        lista.forEach((pos, idx) => {
            const marker = new window.google.maps.Marker({
                position: pos,
                draggable: !soloConsulta,
                label: { text: (idx + 1).toString(), color: 'white' },
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: idx === 0 ? '#22C55E' : idx === lista.length - 1 ? '#EF4444' : '#334eff',
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: 'white',
                    scale: 10
                }
            });

            // Actualizar posición al soltar el arrastre
            marker.addListener('dragend', (event) => {
                const nuevaPos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
                setPuntos((prev) => {
                    const copia = [...prev];
                    copia[idx] = nuevaPos;
                    return copia;
                });
            });

            nuevosOverlays.push(marker);
        });

        setOverlays(nuevosOverlays);
    };

    const handleMapClick = (event) => {
        if (soloConsulta) return;
        const nuevaCoord = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        setPuntos((prev) => [...prev, nuevaCoord]);
    };

    const calcularRutaAutomatica = () => {
        if (puntos.length < 2) return;

        // Google Directions API acepta máximo 25 waypoints.
        // Si hay muchos puntos (de un Auto Ruta previo), muestreamos para quedarnos con ~8 clave.
        let waypointsParaEnviar = puntos.slice(1, -1);
        const MAX_WAYPOINTS = 8;
        if (waypointsParaEnviar.length > MAX_WAYPOINTS) {
            const paso = Math.ceil(waypointsParaEnviar.length / MAX_WAYPOINTS);
            waypointsParaEnviar = waypointsParaEnviar.filter((_, i) => i % paso === 0);
        }

        directionsServiceRef.current.route(
            {
                origin: puntos[0],
                destination: puntos[puntos.length - 1],
                waypoints: waypointsParaEnviar.map((p) => ({ location: p, stopover: true })),
                travelMode: window.google.maps.TravelMode.DRIVING
            },
            (result, status) => {
                if (status === 'OK') {
                    // Actualizamos los puntos con el camino real de la calle
                    const path = result.routes[0].overview_path;
                    const coordsCalle = path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
                    setPuntos(coordsCalle);

                    toast.current.show({
                        severity: 'info',
                        summary: 'Ruta Optimizada',
                        detail: 'Se ha ajustado la ruta a las calles.'
                    });
                } else {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo trazar ruta por calles.'
                    });
                }
            }
        );
    };

    const onMapReady = (event) => {
        mapRef.current = event.map;
        directionsRendererRef.current.setMap(event.map);
    };

    const limpiarMapa = () => {
        setPuntos([]);
        setOverlays([]);
        setSearchQuery('');
        directionsRendererRef.current.setDirections({ routes: [] });
    };

    const handleSave = () => {
        if (!nombre || puntos.length < 2) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atención',
                detail: 'Asigna un nombre y al menos 2 puntos.'
            });
            return;
        }
        const puntosString = puntos.map((p) => `${p.lat} ${p.lng}`).join(', ');
        onConfirmar({
            nombre: nombre.toUpperCase(),
            ruta: `LINESTRING (${puntosString})`,
            rawPuntos: JSON.stringify(puntos)
        });
    };

    useEffect(() => {
        if (datRuta?.ubicacionId) setNombre(datRuta.nombre);
    }, [datRuta]);

    if (!mapReady) return <div className="p-5 text-center">Cargando Mapas...</div>;

    return (
        <div className="flex flex-column gap-3">
            <Toast ref={toast} />
            <div className="formgrid grid p-fluid">
                <div className="field col-12 md:col-8">
                    <label className="font-bold">Nombre de Ruta:</label>
                    <InputText
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
                    />
                </div>
                <div className="field col-12 md:col-4 flex align-items-end">
                    <Button
                        type="button"
                        label="Auto Ruta"
                        icon="pi pi-directions"
                        className="p-button-success"
                        onClick={calcularRutaAutomatica}
                        disabled={puntos.length < 2}
                    />
                </div>

                <div className="field col-12">
                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">
                            <i className="pi pi-search"></i>
                        </span>
                        <InputText
                            ref={inputSearchRef}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Busca un punto de referencia..."
                        />
                        <Button
                            icon="pi pi-times"
                            className="p-button-danger"
                            onClick={() => {
                                setSearchQuery('');
                                setOverlays((prev) => prev.filter((o) => o.get('tipo') !== 'busqueda'));
                            }}
                        />
                    </div>
                </div>

                <div className="field col-12">
                    <div className="surface-border border-2 border-round shadow-2">
                        <GMap
                            onMapReady={onMapReady}
                            options={{ center: currentCenter, zoom: 15 }}
                            overlays={overlays}
                            style={{ width: '100%', height: '450px' }}
                            onMapClick={handleMapClick}
                        />
                    </div>
                </div>

                <div className="field col-12 flex justify-content-between">
                    <Button
                        label="Limpiar Mapa"
                        icon="pi pi-trash"
                        className="p-button-danger p-button-outlined"
                        onClick={limpiarMapa}
                    />
                    <div className="flex gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-secondary" onClick={onCancelar} />
                        <Button
                            label="Guardar Ruta"
                            icon="pi pi-save"
                            className="p-button-primary"
                            onClick={handleSave}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DibujadorDeRuta;
